import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import styled from 'styled-components'
import { pixelCorners, pixelShadow } from '../pixel/pixelUI'

const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(26, 20, 32, 0.82);
  backdrop-filter: blur(5px);
`

const Dialog = styled(motion.section)`
  width: min(68rem, 100%);
  min-width: 0;
  max-height: 94vh;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  background: ${(props) => props.theme.body};
  border: 4px solid ${(props) => props.theme.black};
  ${pixelCorners(12)}
  ${(props) => pixelShadow(9, 9, props.$accent)}
`

const Header = styled.header`
  width: 100%;
  min-width: 0;
  min-height: 4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 1rem;
  color: ${(props) => props.theme.black};
  background: ${(props) => props.$accent};
  border-bottom: 4px solid ${(props) => props.theme.black};

  @media (max-width: 620px) {
    gap: 0.4rem;
    padding: 0.7rem;
  }
`

const HeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  p {
    font-family: ${(props) => props.theme.pixelFont};
    font-size: 0.46rem;
    margin-bottom: 0.4rem;
  }

  h2 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: clamp(0.9rem, 2.5vw, 1.4rem);
  }
`

const HeaderActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.7rem;

  @media (max-width: 620px) {
    gap: 0.35rem;
  }
`

const Counter = styled.span`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.5rem;
  white-space: nowrap;

  @media (max-width: 620px) {
    font-size: 0.4rem;
  }
`

const CloseButton = styled.button`
  width: 2.3rem;
  height: 2.3rem;
  display: grid;
  place-items: center;
  color: #fff;
  background: ${(props) => props.theme.black};
  border: 3px solid ${(props) => props.theme.black};
  font-size: 1.2rem;
  cursor: pointer;
  ${pixelCorners(4)}

  @media (max-width: 620px) {
    width: 2rem;
    height: 2rem;
  }

  &:hover,
  &:focus-visible {
    color: ${(props) => props.$accent};
    outline: 3px solid #fff;
  }
`

const Viewer = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem;

  @media (max-width: 620px) {
    grid-template-columns: 1fr 1fr;
    gap: 0.7rem;
    padding: 0.8rem;
  }
`

const ScreenStage = styled.div`
  min-width: 0;
  height: min(60vh, 42rem);
  overflow: auto;
  display: flex;
  align-items: ${(props) => (props.$zoomed ? 'flex-start' : 'center')};
  justify-content: center;
  padding: 1rem;
  background:
    linear-gradient(${(props) => props.$accent}20 1px, transparent 1px),
    linear-gradient(90deg, ${(props) => props.$accent}20 1px, transparent 1px),
    #fff;
  background-size: 18px 18px;
  border: 4px solid ${(props) => props.theme.black};
  ${pixelCorners(8)}

  @media (max-width: 620px) {
    grid-column: 1 / -1;
    grid-row: 1;
    height: 58vh;
  }
`

const ScreenshotButton = styled.button`
  display: block;
  max-width: 100%;
  padding: 0;
  background: none;
  border: 0;
  cursor: ${(props) => (props.$zoomed ? 'zoom-out' : 'zoom-in')};

  img {
    display: block;
    width: ${(props) => (props.$zoomed ? 'min(44rem, 180vw)' : 'auto')};
    max-width: ${(props) => (props.$zoomed ? 'none' : '100%')};
    max-height: ${(props) => (props.$zoomed ? 'none' : '54vh')};
    object-fit: contain;
    border: 3px solid ${(props) => props.theme.black};
    box-shadow: 6px 6px 0 ${(props) => props.$accent};
  }
`

const NavButton = styled.button`
  width: 3rem;
  height: 3rem;
  display: grid;
  place-items: center;
  color: ${(props) => props.theme.black};
  background: ${(props) => props.$accent};
  border: 3px solid ${(props) => props.theme.black};
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 1rem;
  cursor: pointer;
  ${pixelCorners(5)}
  ${(props) => pixelShadow(4, 4)}

  &:hover,
  &:focus-visible {
    color: ${(props) => props.$accent};
    background: ${(props) => props.theme.black};
    outline: none;
  }

  @media (max-width: 620px) {
    width: 100%;
    grid-row: 2;
  }
`

const GalleryFooter = styled.footer`
  min-width: 0;
  overflow: hidden;
  padding: 0.85rem 1rem 1rem;
  background: #fff;
  border-top: 4px solid ${(props) => props.theme.black};
`

const Hint = styled.p`
  margin-bottom: 0.7rem;
  text-align: center;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.44rem;
  color: ${(props) => props.theme.text};

  @media (max-width: 620px) {
    font-size: 0.38rem;
    line-height: 1.6;
  }
`

const Thumbnails = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  overflow-x: auto;
  padding: 0.25rem 0.25rem 0.45rem;

  @media (max-width: 620px) {
    justify-content: flex-start;
  }
`

const Thumbnail = styled.button`
  flex: 0 0 3.4rem;
  height: 4.8rem;
  overflow: hidden;
  padding: 0;
  background: #fff;
  border: 3px solid ${(props) => (props.$active ? props.$accent : props.theme.black)};
  box-shadow: ${(props) => (props.$active ? `4px 4px 0 ${props.theme.black}` : 'none')};
  cursor: pointer;
  transform: ${(props) => (props.$active ? 'translateY(-3px)' : 'none')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`

const ProjectGallery = ({ gallery, onClose }) => {
  const [index, setIndex] = useState(0)
  const [zoomed, setZoomed] = useState(false)
  const total = gallery.images.length

  const move = (amount) => {
    setIndex((current) => (current + amount + total) % total)
    setZoomed(false)
  }

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') move(-1)
      if (event.key === 'ArrowRight') move(1)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  })

  return (
    <Backdrop
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="gallery-title"
        $accent={gallery.accent}
        initial={{ opacity: 0, scale: 0.88, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 18 }}
      >
        <Header $accent={gallery.accent}>
          <HeaderText>
            <p>{gallery.label}</p>
            <h2 id="gallery-title">{gallery.title}</h2>
          </HeaderText>
          <HeaderActions>
            <Counter>{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</Counter>
            <CloseButton $accent={gallery.accent} onClick={onClose} aria-label="Close project gallery">×</CloseButton>
          </HeaderActions>
        </Header>

        <Viewer>
          <NavButton $accent={gallery.accent} onClick={() => move(-1)} aria-label="Previous screenshot">‹</NavButton>
          <ScreenStage $accent={gallery.accent} $zoomed={zoomed}>
            <ScreenshotButton
              $accent={gallery.accent}
              $zoomed={zoomed}
              onClick={() => setZoomed((current) => !current)}
              aria-label={zoomed ? 'Zoom out screenshot' : 'Zoom in screenshot'}
            >
              <img src={gallery.images[index]} alt={`${gallery.title} app screen ${index + 1}`} decoding="async" />
            </ScreenshotButton>
          </ScreenStage>
          <NavButton $accent={gallery.accent} onClick={() => move(1)} aria-label="Next screenshot">›</NavButton>
        </Viewer>

        <GalleryFooter>
          <Hint>SELECT A SCREEN · CLICK MAIN IMAGE TO ZOOM · USE ← → KEYS</Hint>
          <Thumbnails>
            {gallery.images.map((image, imageIndex) => (
              <Thumbnail
                key={image}
                $accent={gallery.accent}
                $active={imageIndex === index}
                onClick={() => { setIndex(imageIndex); setZoomed(false) }}
                aria-label={`View screenshot ${imageIndex + 1}`}
              >
                <img src={image} alt="" loading="lazy" decoding="async" />
              </Thumbnail>
            ))}
          </Thumbnails>
        </GalleryFooter>
      </Dialog>
    </Backdrop>
  )
}

export default ProjectGallery
